import { useEffect, useMemo, useState } from "react";
import { useProjectActions } from "@contexts/projects";

const GROUP_LABELS = {
  internal: 'Usuários internos (Ampli / Cartesian)',
  company: 'Usuários da construtora',
}

const userLabel = (user) => user.name || user.email || user._id

/**
 * Painel "Acesso à Obra" — MANUTENÇÃO do acesso de uma obra já ingerida, com uma tela de
 * confirmação antes de gravar. A liberação INICIAL acontece no fluxo de "Registrar dados"
 * (`BuildingFirstRunModal.jsx`), disparado da listagem de obras.
 *
 * Duas coisas a saber antes de mexer aqui:
 *
 * 1. O que habilita acesso é o `_id` de `wbs_buildings`, que só existe depois da primeira
 *    execução do pipeline. Por isso o painel fica inerte enquanto `ingested` é falso — não há
 *    id para gravar em `users.buildingIds`.
 * 2. A seleção é a lista COMPLETA de quem deve ter acesso: candidato desmarcado tem o acesso
 *    revogado. É exatamente por isso que existe o passo de confirmação — sem ele, abrir o
 *    painel e salvar sem olhar removeria acesso de quem já tinha.
 */
const BuildingAccessPanel = ({ buildingId }) => {
  const [data, setData] = useState(null)
  const [selected, setSelected] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const { getBuildingAccess, updateBuildingAccess } = useProjectActions();

  const load = async () => {
    setLoading(true)
    setConfirming(false)
    const res = await getBuildingAccess(buildingId)
    if (res?.ok) {
      setData(res)
      setSelected((res.users || []).filter(u => u.has_access).map(u => u._id))
    } else {
      setFeedback({ ok: false, message: res?.message || 'Não foi possível carregar os acessos.' })
    }
    setLoading(false)
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId])

  const users = data?.users || []
  const ingested = data?.ingested === true

  const toggle = (userId) => {
    setSelected(prev => (prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]))
  }

  const selectAll = (group) => {
    const groupIds = users.filter(u => u.group === group && !u.locked).map(u => u._id)
    setSelected(prev => [...new Set([...prev, ...groupIds])])
  }

  const clearGroup = (group) => {
    const groupIds = new Set(users.filter(u => u.group === group).map(u => u._id))
    setSelected(prev => prev.filter(id => !groupIds.has(id)))
  }

  // O diff é o conteúdo da tela de confirmação: o operador precisa ver o que MUDA, não só
  // quem ficou marcado.
  const diff = useMemo(() => {
    const selectedSet = new Set(selected)
    const grant = users.filter(u => !u.locked && selectedSet.has(u._id) && !u.has_access)
    const revoke = users.filter(u => !u.locked && !selectedSet.has(u._id) && u.has_access)
    const keep = users.filter(u => selectedSet.has(u._id) && u.has_access)
    return { grant, revoke, keep }
  }, [users, selected])

  const hasChanges = diff.grant.length > 0 || diff.revoke.length > 0

  const apply = async () => {
    const res = await updateBuildingAccess(buildingId, selected)
    setFeedback({ ok: !!res?.ok, message: res?.message || 'Não foi possível atualizar os acessos.' })
    if (res?.ok) await load()
    else setConfirming(false)
  }

  const groups = ['internal', 'company']

  return (
    <fieldset className="building-access-fieldset">
      <legend>Acesso à obra</legend>

      {!loading && !ingested && (
        <p>
          Disponível após a primeira execução do pipeline — use o botão "Registrar dados" na
          listagem de obras, que já pede a liberação dos usuários. O vínculo de acesso usa o id
          de medição da obra, que só passa a existir depois da ingestão.
        </p>
      )}

      {loading && <p>Carregando usuários...</p>}

      {ingested && !loading && data && !confirming && (
        <>
          {groups.map(group => {
            const groupUsers = users.filter(u => u.group === group)
            if (groupUsers.length === 0) return null
            return (
              <div className="access-group" key={group}>
                <div className="access-group-header">
                  <strong>{GROUP_LABELS[group]}</strong>
                  <button type="button" className="button" onClick={() => selectAll(group)}>Marcar todos</button>
                  <button type="button" className="button" onClick={() => clearGroup(group)}>Desmarcar todos</button>
                </div>
                {groupUsers.map(user => (
                  <label key={user._id} className="access-user-row">
                    <input
                      type="checkbox"
                      checked={selected.includes(user._id)}
                      disabled={user.locked}
                      onChange={() => toggle(user._id)}
                    />
                    {userLabel(user)}
                    {user.email && user.name && <span className="access-user-email"> — {user.email}</span>}
                    {user.active === false && <span className="inactive-tag">Inativo</span>}
                    {user.locked && <span className="inactive-tag">Conta de apresentação</span>}
                  </label>
                ))}
              </div>
            )
          })}

          <div className="edit-user-actions">
            <button
              type="button"
              className="button button-accent"
              disabled={!hasChanges}
              onClick={() => setConfirming(true)}
            >
              Revisar e confirmar
            </button>
            <button type="button" className="button" onClick={load}>Descartar alterações</button>
          </div>
          {!hasChanges && <p>Nenhuma alteração pendente.</p>}
        </>
      )}

      {ingested && confirming && (
        <div className="access-confirm">
          <p>Confirme quem passa a ter acesso a "{data?.building?.building_name}":</p>

          <p><strong>Vão receber acesso ({diff.grant.length})</strong></p>
          {diff.grant.length === 0 ? <p>—</p> : (
            <ul>{diff.grant.map(u => <li key={u._id}>{userLabel(u)} ({GROUP_LABELS[u.group]})</li>)}</ul>
          )}

          <p><strong>Vão PERDER acesso ({diff.revoke.length})</strong></p>
          {diff.revoke.length === 0 ? <p>—</p> : (
            <ul>{diff.revoke.map(u => <li key={u._id}>{userLabel(u)} ({GROUP_LABELS[u.group]})</li>)}</ul>
          )}

          <p><strong>Mantêm o acesso ({diff.keep.length})</strong></p>

          <div className="edit-user-actions">
            <button type="button" className="button button-accent" onClick={apply}>Confirmar</button>
            <button type="button" className="button" onClick={() => setConfirming(false)}>Voltar</button>
          </div>
        </div>
      )}

      {feedback && (
        <p className={feedback.ok ? 'directory-feedback directory-feedback--ok' : 'directory-feedback directory-feedback--error'}>
          {feedback.message}
        </p>
      )}
    </fieldset>
  );
};

export default BuildingAccessPanel;
